<template>
    <v-card v-if="schemaConfig">
        <v-row>
            <v-col v-if="isTemplateConfig">
                <v-alert color="warning">
                    {{ display }}
                </v-alert>
            </v-col>
            <v-col v-else>
                <v-card-title>
                    {{ display }}
                    <v-icon-btn v-if="!isProtocolView"
                    ><v-icon @click="showEditSchemaConfigNameForm()"
                    >$edit</v-icon
                    ></v-icon-btn
                    >
                </v-card-title>
                <v-card-subtitle v-if="!isProtocolView">
                    {{ schemaConfigIdLabel }}
                </v-card-subtitle>
                <v-expansion-panels class="ms-4 mt-2">
                    <v-expansion-panel>
                        <v-expansion-panel-title
                        >Related Experiment Groups ({{relatedExperimentGroups.length}})</v-expansion-panel-title
                        >
                        <v-expansion-panel-text>
                            <v-row v-for="item in relatedExperimentGroups" :key="item.id" class="ms-2 my-4">
                                {{ item.name }}
                                (<v-btn append-icon="$open-in-new" slim color="primary" variant="text" density="compact" size="medium" @click="goToExperimentGroupPage(item.code)">{{ item.code }}</v-btn>)
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
            <v-col cols="4">
                <v-row class="mt-2 mr-4" justify="end">
                    <v-btn
                        v-if="!isTemplateConfig && tab === 'ThingSchema'&& isEditTemplateAllowed"
                        @click="viewTemplates"
                        color="primary"
                        class="mt-2"
                    >Edit Templates</v-btn
                    >
                </v-row>
            </v-col>
        </v-row>
        <v-card-text>
            <v-tabs v-model="tab" bg-color="info" theme="dark">
                <v-menu>
                    <template v-slot:activator="{ props }">
                        <v-btn
                            class="align-self-center"
                            height="100%"
                            rounded="0"
                            variant="plain"
                            v-bind="props"
                        >Schemas</v-btn
                        >
                    </template>

                    <v-list class="bg-grey-lighten-3">
                        <v-list-item
                            v-for="thingSchema in schemaConfig?.thingSchemas"
                            :key="thingSchema.id"
                            :title="thingSchema.displayName"
                            @click="viewThingSchema(thingSchema)"
                        ></v-list-item>
                        <v-list-item key="addSchema" @click="showAddThingSchemaForm">
                            Add Schema
                        </v-list-item>
                    </v-list>
                </v-menu>
                <v-tab v-if="!isTemplateConfig" value="TaskSchedule">Task Schedule</v-tab>
                <v-tab v-if="!isTemplateConfig" value="ExperimentSetup">Experiment Setup</v-tab>
                <v-tab v-if="!isTemplateConfig" value="SchemaHierarchy">Schema Hierarchy</v-tab>
            </v-tabs>
            <thing-schema-tab
                v-if="tab === 'ThingSchema'"
                :schema-config="schemaConfig"
                :all-thing-schemas="schemaConfig!.thingSchemas"
                :thing-schema="selectedThingSchema!"
                @thingSchemaSaved="loadSchemaConfig"
            />
            <task-schedule-tab
                v-if="tab === 'TaskSchedule' && isProtocolView"
                :protocol-code="schemaConfig!.code"
            />
            <schema-config-task-schedule
                v-if="tab === 'TaskSchedule' && !isProtocolView"
                :schema-config="schemaConfig as unknown as SchemaConfigAdminConfig"
            />
            <schema-config-experiment-setup-tab
                v-if="tab == 'ExperimentSetup' && !isTemplateConfig"
                :schema-config="schemaConfig as unknown as SavedSchemaConfigDTO"
            />
            <protocol-hierarchy
                v-if="tab === 'SchemaHierarchy' && !isTemplateConfig"
                :schema-config="schemaConfig"
                @viewThingSchema="viewThingSchemaName"
            />
        </v-card-text>
    </v-card>
    <v-dialog v-model="editSchemaConfigNameDialogVisible">
        <schema-config-form
            :schema-config="((schemaConfig!) as unknown as SavedSchemaConfigDTO)"
            @cancelForm="hideSchemaConfigForm"
            @schemaConfigSaved="editedSchemaConfigName"
        />
    </v-dialog>
    <v-dialog v-model="addThingSchemaDialogVisible">
        <add-thing-schema-form
            :existingThingSchemaNames="existingThingSchemaNames"
            :isTemplateConfig="isTemplateConfig"
            @cancel-form="cancelAddThingSchemaForm"
            @add-thing-schema-templates="addThingSchemaTemplates"
            @addBlankThingSchema="addBlankThingSchema"
        />
    </v-dialog>
</template>
<script setup lang="ts">
import { ref, nextTick, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import _ from "lodash";
import { withLoadingScreen } from "@/lib/loading";
import TaskScheduleTab from "./ProtocolTaskSchedule.vue";
import ProtocolHierarchy from "./ProtocolHierarchy.vue";
import ThingSchemaTab from "@/components/SchemaConfig/ThingSchemaTab.vue";
import {
    manageSchemasAPI,
    AbstractSchemaConfig,
    SavedThingSchemaTemplate,
    ThingSchemaAdminConfig,
    SavedSchemaConfigDTO,
    SchemaConfigAdminConfig,
} from "@/services/schemas/manageSchemasAPI";
import AddThingSchemaForm from "@/components/SchemaConfig/AddThingSchemaForm.vue";
import { useAuth } from "@/lib/ts/auth";
import SchemaConfigForm from "@/components/SchemaConfig/SchemaConfigForm.vue";
import SchemaConfigTaskSchedule from "@/components/SchemaConfig/SchemaConfigTaskSchedule.vue";
import SchemaConfigExperimentSetupTab from "@/components/SchemaConfig/SchemaConfigExperimentSetupTab.vue";
import {
    experimentGroupsAPI,
    ExperimentGroupDTO,
} from "@/services/experimentGroups/experimentGroupsAPI";

const route = useRoute();
const router = useRouter();
const toast = useToast();
const auth = useAuth();

const schemaConfig = ref<AbstractSchemaConfig>();
const selectedThingSchema = ref<ThingSchemaAdminConfig>();
const editSchemaConfigNameDialogVisible = ref<boolean>(false);
const tab = ref("SchemaHierarchy");
const addThingSchemaDialogVisible = ref(false);

const allExperimentGroups = ref<ExperimentGroupDTO[]>([]);
const relatedExperimentGroups = computed(() => {
    if (!isProtocolView.value) {
        const schemaConfigId = (schemaConfig.value as unknown as SavedSchemaConfigDTO).id;
        return allExperimentGroups.value.filter((group) => {
            return group.schemaConfigId === schemaConfigId;
        });
    }
    return [];
});

watch(
    () => route.path,
    () => {
        loadSchemaConfig();
    }
);

const isProtocolView = computed(() => {
    return _.isNil(route.params.schemaConfigCode);
});

const existingThingSchemaNames = computed(() => {
    return schemaConfig.value!.thingSchemas.map((s) => s.name);
});

const display = computed(() => {
    if (isTemplateConfig.value) {
        return "Template Schemas";
    } else {
        return `${schemaConfig.value!.name} - ${schemaConfig.value!.code}`;
    }
});

const schemaConfigIdLabel = computed(() => {
    if (!isProtocolView.value) {
        return (schemaConfig.value as unknown as SavedSchemaConfigDTO).id;
    }
    return "";
})

const isTemplateConfig = computed(() => {
    return route.params.schemaConfigCode === "template" || route.params.protocolCode === "template";
});

const isEditTemplateAllowed = computed(() => {
    return auth.isAuthorized("SchemaTemplates:Manage");
});

function showEditSchemaConfigNameForm() {
    editSchemaConfigNameDialogVisible.value = true;
}

function hideSchemaConfigForm() {
    editSchemaConfigNameDialogVisible.value = false;
}

async function loadSchemaConfig() {
    await withLoadingScreen(async () => {
        if (isTemplateConfig.value) {
            if (!isEditTemplateAllowed) {
                toast.error("You do not have access to manage template schemas", {
                    timeout: false,
                });
            } else {
                schemaConfig.value = await manageSchemasAPI.fetchTemplateSchemaConfig();
            }
        } else {
            if (isProtocolView.value) {
                schemaConfig.value = await manageSchemasAPI.fetchProtocolAdminConfig(
                    route.params.protocolCode! as string
                );
            } else {
                schemaConfig.value = await manageSchemasAPI.fetchSchemaConfigAdminConfig(
                    route.params.schemaConfigCode! as string
                );
                allExperimentGroups.value = await experimentGroupsAPI.fetchExperimentGroups();
            }
        }
        showSchemaConfigTab();
    });
}

async function editedSchemaConfigName() {
    await withLoadingScreen(async () => {
        hideSchemaConfigForm();
        await loadSchemaConfig();
    });
}

function viewTemplates() {
    let routeParams = {};
    if (isProtocolView.value) {
        routeParams = {
            protocolCode: "template",
        };
    } else {
        routeParams = {
            schemaConfigCode: "template",
        };
    }
    if (selectedThingSchema.value) {
        routeParams["thingSchemaName"] = selectedThingSchema.value.name;
    }
    router.push({
        name: isProtocolView.value
            ? "SchemaConfigViewForProtocol"
            : "SchemaConfigViewForSchemaConfig",
        params: routeParams,
    });
}

function viewThingSchemaName(thingSchemaName: string) {
    const thingSchema = schemaConfig.value!.thingSchemas.find((s) => s.name === thingSchemaName)!;
    viewThingSchema(thingSchema);
}

function showAddThingSchemaForm() {
    addThingSchemaDialogVisible.value = true;
}

function cancelAddThingSchemaForm() {
    addThingSchemaDialogVisible.value = false;
}

async function addThingSchemaTemplates(thingSchemaTemplates: SavedThingSchemaTemplate[]) {
    await withLoadingScreen(async () => {
        const thingSchemaTemplateIds = thingSchemaTemplates.map((s) => s.id);
        if (isProtocolView.value) {
            await manageSchemasAPI.addTemplateThingSchemasToProtocol(
                schemaConfig.value!.code,
                thingSchemaTemplateIds
            );
        } else {
            await manageSchemasAPI.addTemplateThingSchemasToSchemaConfig(
                (schemaConfig.value! as unknown as SavedSchemaConfigDTO).id,
                thingSchemaTemplateIds
            );
        }
        await loadSchemaConfig();
        addThingSchemaDialogVisible.value = false;
    });
}

async function addBlankThingSchema(name: string) {
    await withLoadingScreen(async () => {
        await manageSchemasAPI.createBlankThingSchemaTemplate(name);
        await loadSchemaConfig();
        addThingSchemaDialogVisible.value = false;
    });
}

function viewThingSchema(thingSchema: ThingSchemaAdminConfig) {
    if (!_.isNil(thingSchema)) {
        selectedThingSchema.value = thingSchema;
        updateRoute(thingSchema);
        nextTick().then(() => (tab.value = "ThingSchema"));
    }
}

function updateRoute(thingSchema: ThingSchemaAdminConfig) {
    if (isProtocolView.value) {
        router.replace({
            name: "SchemaConfigViewForProtocol",
            params: {
                protocolCode: schemaConfig.value!.code,
                thingSchemaName: thingSchema.name,
            },
        });
    } else {
        router.replace({
            name: "SchemaConfigViewForSchemaConfig",
            params: {
                schemaConfigCode: schemaConfig.value!.code,
                thingSchemaName: thingSchema.name,
            },
        });
    }
}

/**
 * Shows the schema tab if the schema is in the URL
 */
function showSchemaConfigTab() {
    const linkedSchema = getLinkedSchema();
    if (linkedSchema) {
        viewThingSchema(linkedSchema);
    } else if (hasSchemas()) {
        const thingSchema = schemaConfig.value!.thingSchemas[0];
        viewThingSchema(thingSchema);
    }
}

function hasSchemas() {
    return schemaConfig.value!.thingSchemas.length > 0;
}

function getLinkedSchema(): ThingSchemaAdminConfig | undefined {
    const linkedSchemaName = route.params.thingSchemaName;
    if (linkedSchemaName) {
        return schemaConfig.value!.thingSchemas.find((s) => s.name === linkedSchemaName);
    } else {
        return undefined;
    }
}

function goToExperimentGroupPage(experimentGroupCode: string) {
    router.push({ path: `/experiment-groups/${experimentGroupCode}` });
}

loadSchemaConfig();
</script>
